import React, { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, DollarSign, AlertTriangle, Clock, Edit3, Check, X } from 'lucide-react';
import api from '../../services/api';

interface WorkingRate {
  id: number;
  from_currency: string;
  to_currency: string;
  bank_rate: number | null;
  market_rate: number;
  working_rate: number;
  buffer_percent: number;
  rate_source: string;
  trigger_reason: string;
  is_override: boolean;
  created_at: string;
  created_by: string;
}

interface InrRate {
  from: string;
  to: string;
  xe_rate: number;
  markup: number;
  applied_rate: number;
}

interface LiveRates {
  bank_rate_usd_idr: number | null;
  market_rate_usd_idr: number | null;
  working_rate: WorkingRate | null;
  inr_rate: InrRate | null;
}

interface RateHistory {
  id: number;
  bank_rate: number | null;
  market_rate: number;
  working_rate: number;
  buffer_percent: number;
  rate_source: string;
  trigger_reason: string;
  is_active: boolean;
  is_override: boolean;
  created_at: string;
  created_by: string;
}

const FxRatePanel: React.FC = () => {
  const [liveRates, setLiveRates] = useState<LiveRates | null>(null);
  const [history, setHistory] = useState<RateHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideValue, setOverrideValue] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState<{ running: boolean; jobs: any[] } | null>(null);
  const [togglingScheduler, setTogglingScheduler] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ratesRes, historyRes] = await Promise.all([
        api.get('/fx/live-rates'),
        api.get('/fx/history?limit=20'),
      ]);
      if (ratesRes.data.success) setLiveRates(ratesRes.data.data);
      if (historyRes.data.success) setHistory(historyRes.data.data);

      // Scheduler status (no auth needed)
      try {
        const statusRes = await fetch('/api/fx/scheduler-status');
        const statusData = await statusRes.json();
        setSchedulerStatus(statusData);
      } catch (e) {
        console.error('Error fetching scheduler status:', e);
      }
    } catch (error) {
      console.error('Error loading FX data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerUpdate = async () => {
    if (!window.confirm('Check if the working rate needs updating based on market movement and time rules?')) return;
    setUpdating(true);
    try {
      const res = await api.post('/fx/check-update');
      if (res.data.success) {
        const d = res.data.data;
        if (d.updated) {
          alert(`Working rate updated to ${d.working_rate} IDR/USD (${d.trigger_reason}, source: ${d.rate_source})`);
        } else {
          alert(d.reason || 'No update needed');
        }
        loadData();
      }
    } catch (error) {
      console.error('Error triggering update:', error);
      alert('Error triggering rate check');
    } finally {
      setUpdating(false);
    }
  };

  const saveOverride = async () => {
    const rate = parseFloat(overrideValue);
    if (!rate || rate <= 0) {
      alert('Enter a valid rate');
      return;
    }
    if (!window.confirm(`Set working rate to ${rate.toLocaleString()} IDR/USD? This overrides the calculated rate.`)) return;
    setSavingOverride(true);
    try {
      const res = await api.post('/fx/override', { working_rate: rate });
      if (res.data.success) {
        alert(`Working rate overridden to ${rate.toLocaleString()}`);
        setOverrideMode(false);
        setOverrideValue('');
        loadData();
      }
    } catch (error) {
      console.error('Error saving override:', error);
      alert('Error saving override');
    } finally {
      setSavingOverride(false);
    }
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate == null) return '—';
    return rate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading FX rates...</span>
      </div>
    );
  }

  const wr = liveRates?.working_rate;
  const inr = liveRates?.inr_rate;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">💱 FX Rate Management</h1>
        <p className="text-gray-600 text-sm">Manage USD→IDR working rate and INR payment conversion</p>
      </div>

      {/* Live Rates Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Working Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Working Rate</span>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {wr ? formatRate(wr.working_rate) : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            IDR per USD {wr?.is_override && <span className="text-orange-600 font-medium">(Override)</span>}
          </div>
        </div>

        {/* Bank Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Bank Rate (Mandiri)</span>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatRate(liveRates?.bank_rate_usd_idr)}
          </div>
          <div className="text-xs text-gray-500 mt-1">TT Counter Buy</div>
        </div>

        {/* Market Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Market Rate</span>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatRate(liveRates?.market_rate_usd_idr)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Frankfurter mid-market</div>
        </div>

        {/* INR Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">INR Payment Rate</span>
            <DollarSign className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {inr ? formatRate(inr.applied_rate) : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            INR per USD (XE {inr ? formatRate(inr.xe_rate) : '—'} + {inr?.markup ?? '—'})
          </div>
        </div>
      </div>

      {/* Working Rate Details + Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Current Rate Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Working Rate</h2>
          {wr ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Rate</span>
                <span className="font-semibold">{formatRate(wr.working_rate)} IDR/USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Source</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  wr.rate_source === 'bank' ? 'bg-green-100 text-green-700' :
                  wr.rate_source === 'manual' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {wr.rate_source === 'bank' ? 'Bank Rate' : wr.rate_source === 'manual' ? 'Manual Override' : 'Market Fallback'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Buffer</span>
                <span>{wr.buffer_percent}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Trigger</span>
                <span>{wr.trigger_reason}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Updated</span>
                <span>{formatDate(wr.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">By</span>
                <span>{wr.created_by || '—'}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p>No working rate configured</p>
              <p className="text-xs mt-1">Trigger an update to set the initial rate</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
          <div className="space-y-4">
            {/* Check & Update */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900 text-sm">Check & Update Rate</h3>
                  <p className="text-xs text-blue-700 mt-1">
                    Fetches live bank & market rates. Updates working rate only if market moved ≥2% or 14+ days since last update.
                  </p>
                  <button
                    onClick={triggerUpdate}
                    disabled={updating}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {updating && <RefreshCw className="w-4 h-4 animate-spin" />}
                    {updating ? 'Checking...' : 'Check Now'}
                  </button>
                </div>
              </div>
            </div>

            {/* Manual Override */}
            <div className="p-4 bg-orange-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Edit3 className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium text-orange-900 text-sm">Manual Override</h3>
                  <p className="text-xs text-orange-700 mt-1">
                    Manually set the working rate. Overrides the calculated rate until the next automatic update.
                  </p>
                  {overrideMode ? (
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        value={overrideValue}
                        onChange={(e) => setOverrideValue(e.target.value)}
                        placeholder="e.g. 16300"
                        className="px-3 py-2 border border-orange-300 rounded-lg text-sm w-40 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <span className="text-xs text-gray-500">IDR/USD</span>
                      <button
                        onClick={saveOverride}
                        disabled={savingOverride}
                        className="p-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setOverrideMode(false); setOverrideValue(''); }}
                        className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setOverrideMode(true)}
                      className="mt-3 px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 flex items-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Set Manual Rate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduler Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Daily Scheduler</h2>
          </div>
          <div className="flex items-center gap-2">
            {schedulerStatus && (
              <button
                onClick={async () => {
                  const action = schedulerStatus.running ? 'stop' : 'start';
                  if (!window.confirm(`Are you sure you want to ${action} the FX scheduler?`)) return;
                  setTogglingScheduler(true);
                  try {
                    const res = await api.post(`/fx/scheduler-${action}`);
                    if (res.data.success) {
                      alert(res.data.message);
                      loadData();
                    } else {
                      alert(res.data.message || `Failed to ${action} scheduler`);
                    }
                  } catch (error: any) {
                    console.error(`Error ${action}ing scheduler:`, error);
                    alert(error?.response?.data?.detail || `Failed to ${action} scheduler`);
                  } finally {
                    setTogglingScheduler(false);
                  }
                }}
                disabled={togglingScheduler}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 disabled:opacity-50 ${
                  schedulerStatus.running
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {togglingScheduler ? '...' : schedulerStatus.running ? '⏹ Stop' : '▶ Start'}
              </button>
            )}
            <button
            onClick={async () => {
              try {
                const token = sessionStorage.getItem('jwt_token');
                const response = await fetch('/api/fx/scheduler-log', {
                  headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                });
                if (!response.ok) {
                  alert('No scheduler log found yet');
                  return;
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'fx_scheduler.log';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Error downloading log:', error);
                alert('Error downloading log file');
              }
            }}
            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1"
          >
            📄 Download Log
          </button>
          </div>
        </div>
        {schedulerStatus ? (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                schedulerStatus.running ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {schedulerStatus.running ? 'Running' : 'Stopped'}
              </span>
            </div>
            {schedulerStatus.jobs.map((job, i) => (
              <div key={i}>
                <div className="flex justify-between">
                  <span className="text-gray-500">Schedule</span>
                  <span className="text-xs">{job.trigger}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Next Run</span>
                  <span className="text-xs font-medium">{job.next_run ? new Date(job.next_run).toLocaleString('en-GB') : '—'}</span>
                </div>
              </div>
            ))}
            {schedulerStatus.jobs.length === 0 && (
              <div className="text-gray-500 text-xs">No scheduled jobs found</div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-sm">Unable to fetch scheduler status</div>
        )}
      </div>

      {/* Rate History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Rate History</h2>
          </div>
          <button
            onClick={loadData}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Working Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Market Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">
                    No rate history yet. Trigger an update to create the first entry.
                  </td>
                </tr>
              ) : (
                history.map((row) => (
                  <tr key={row.id} className={`${row.is_active ? 'bg-green-50' : ''} hover:bg-gray-50`}>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {formatRate(row.working_rate)}
                      {row.is_active && (
                        <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Active</span>
                      )}
                      {row.is_override && (
                        <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Override</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatRate(row.bank_rate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatRate(row.market_rate)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.rate_source === 'bank' ? 'bg-green-100 text-green-700' :
                        row.rate_source === 'manual' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {row.rate_source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.trigger_reason}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.created_by || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FxRatePanel;
